import {
  STATUS_ANALISE_CRITERIO,
  type PatchAnaliseSolicitacaoBody,
  type PersistAnaliseSolicitacaoPayload,
} from "../../../../services";
import type { AnaliseCamposPorSecao, AnaliseStatusUI } from "./buildAnaliseCamposPorSecaoId";

// Converte o estado de análise da UI para payloads de persistência (PATCH atual e PUT legado).
function statusUiParaId(status: AnaliseStatusUI): number {
  switch (status) {
    case "CONFORME":
      return STATUS_ANALISE_CRITERIO.Conforme;
    case "NAO_CONFORME":
      return STATUS_ANALISE_CRITERIO.NaoConforme;
    case "DEVOLVER":
      return STATUS_ANALISE_CRITERIO.Devolver;
    default:
      return STATUS_ANALISE_CRITERIO.Pendente;
  }
}

function resolveCamposAnalisePorId(
  id: string,
  map: Record<string, AnaliseCamposPorSecao>,
): AnaliseCamposPorSecao | undefined {
  const byKey = map[id];
  if (byKey) return byKey;
  const idNum = Number(id);
  if (!Number.isFinite(idNum)) return undefined;
  return Object.values(map).find(
    (c) => typeof c?.criterioIdApi === "number" && c.criterioIdApi === idNum,
  );
}


export function buildPatchAnaliseSolicitacaoPayload(
  map: Record<string, AnaliseCamposPorSecao>,
  receitaOperacional: number,
  associados: number,
  apenasCriterioIds?: Set<string>,
): PatchAnaliseSolicitacaoBody {
  const porCriterioId = new Map<
    number,
    NonNullable<PatchAnaliseSolicitacaoBody["analiseCriterioSolicitacao"]>[0]
  >();
  const pontuar = (
    row: NonNullable<PatchAnaliseSolicitacaoBody["analiseCriterioSolicitacao"]>[0],
  ): number =>
    (row.statusId !== STATUS_ANALISE_CRITERIO.Pendente ? 2 : 0) +
    (row.id > 0 ? 1 : 0);
  for (const [idStr, campos] of Object.entries(map)) {
    if (apenasCriterioIds && !apenasCriterioIds.has(idStr)) continue;
    const keyNum = Number(idStr);
    const keyOk = Number.isFinite(keyNum) && keyNum > 0;
    const fromApi =
      typeof campos.criterioIdApi === "number" &&
      Number.isFinite(campos.criterioIdApi) &&
      campos.criterioIdApi > 0
        ? campos.criterioIdApi
        : null;
    const criterioId = fromApi ?? (keyOk ? keyNum : null);
    if (criterioId == null || criterioId <= 0) continue;
    const registroId =
      typeof campos.registroAnaliseId === "number" &&
      Number.isFinite(campos.registroAnaliseId) &&
      campos.registroAnaliseId > 0
        ? campos.registroAnaliseId
        : 0;
    const candidato = {
      id: registroId,
      criterioId,
      statusId: statusUiParaId(campos.status),
      observacaoAnalista: campos.observacaoAnalista ?? "",
      observacaoInterna: campos.observacaoInterna ?? "",
    };
    const atual = porCriterioId.get(criterioId);
    if (!atual) {
      porCriterioId.set(criterioId, candidato);
      continue;
    }
    // Resolve alias de chaves (ex.: c.id != criterioId da seção):
    // 1) decide qual status/observações é mais confiável (não-pendente > pendente),
    // 2) preserva id existente (>0) para atualizar a linha correta no backend.
    const base = pontuar(candidato) >= pontuar(atual) ? candidato : atual;
    const idResolvido =
      atual.id > 0 ? atual.id : candidato.id > 0 ? candidato.id : 0;
    porCriterioId.set(criterioId, {
      ...base,
      id: idResolvido,
    });
  }
  return {
    receitaOperacional,
    associados,
    analiseCriterioSolicitacao: [...porCriterioId.values()],
  };
}


export function buildPersistAnalisePayload(
  map: Record<string, AnaliseCamposPorSecao>,
  apto: boolean,
  quantidadeAssociados: number,
  apenasCriterioIds?: Set<string>,
): PersistAnaliseSolicitacaoPayload {
  const criterios: PersistAnaliseSolicitacaoPayload["criterios"] = [];
  for (const [idStr, campos] of Object.entries(map)) {
    if (apenasCriterioIds && !apenasCriterioIds.has(idStr)) continue;
    const keyNum = Number(idStr);
    const keyOk = Number.isFinite(keyNum) && keyNum > 0;
    const fromApi =
      typeof campos.criterioIdApi === "number" &&
      Number.isFinite(campos.criterioIdApi) &&
      campos.criterioIdApi > 0
        ? campos.criterioIdApi
        : null;
    const criterioId = fromApi ?? (keyOk ? keyNum : null);
    if (criterioId == null || criterioId <= 0) continue;
    criterios.push({
      criterioId,
      statusId: statusUiParaId(campos.status),
      observacaoAnalista: campos.observacaoAnalista ?? "",
      observacaoInterna: campos.observacaoInterna ?? "",
    });
  }
  return {
    apto,
    quantidadeAssociados,
    criterios,
  };
}

export function validarAnaliseCamposPreenchidos(
  secoesIds: string[],
  map: Record<string, AnaliseCamposPorSecao>,
  titulosPorId: Record<string, string>,
): { ok: true } | { ok: false; mensagens: string[] } {
  const mensagens: string[] = [];
  for (const id of secoesIds) {
    const campos = resolveCamposAnalisePorId(id, map);
    const titulo = titulosPorId[id] ?? id;
    const st = campos?.status ?? "";
    if (st === "NAO_CONFORME") {
      const obs = String(campos?.observacaoAnalista ?? "").trim();
      if (!obs) {
        mensagens.push(`"${titulo}" está como NÃO CONFORME sem observação.`);
      }
    } else if (st === "DEVOLVER") {
      const obs = String(campos?.observacaoAnalista ?? "").trim();
      if (!obs) {
        mensagens.push(`"${titulo}" está como DEVOLVER sem observação.`);
      }
    }
  }
  if (mensagens.length > 0) return { ok: false, mensagens };
  return { ok: true };
}


export function validarNaoConformesObrigatorios(
  obrigatoriosIds: string[],
  map: Record<string, AnaliseCamposPorSecao>,
  titulosPorId: Record<string, string>,
): { ok: true } | { ok: false; mensagens: string[] } {
  const mensagens: string[] = [];
  for (const id of obrigatoriosIds) {
    const campos = resolveCamposAnalisePorId(id, map);
    const st = campos?.status;
    if (st !== "NAO_CONFORME" && st !== "DEVOLVER") continue;
    const titulo = titulosPorId[id] ?? id;
    const obs = String(campos?.observacaoAnalista ?? "").trim();
    if (!obs) {
      mensagens.push(
        st === "DEVOLVER"
          ? `"${titulo}" está como DEVOLVER sem observação.`
          : `"${titulo}" está como NÃO CONFORME sem observação.`,
      );
    }
  }
  if (mensagens.length > 0) return { ok: false, mensagens };
  return { ok: true };
}
